/* ===========================================================
   panels/Share.jsx — Google Docs-style sharing

   Two links, each switched on or off: "can view" and "can edit".
   People who open a link appear under "People with access", where the
   owner can change what they can do or remove them. "Reset" makes a
   new link and the old one stops working.
   =========================================================== */

import { useEffect, useState } from 'preact/hooks';
import { api } from '../lib/api.js';
import { online } from '../state.js';
import { Button, IconButton, Icon, Avatar, toast, Spinner, confirmDialog } from '../ui/ui.jsx';
import { ProtoCard } from '../ui/proto.jsx';

export function Panel({ title, onClose, children, label }) {
  useEffect(() => {
    const k = e => { if (e.key === 'Escape' && !document.body.classList.contains('has-dialog')) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);
  return (
    <aside class="panel" aria-label={label || title}>
      <header class="panel-head"><h2>{title}</h2><IconButton icon="close" label="Close" onClick={onClose} /></header>
      <div class="panel-body">{children}</div>
    </aside>
  );
}

const linkFor = token => `${location.origin}/s/${token}`;

async function copy(text) {
  try { await navigator.clipboard.writeText(text); toast('Link copied. Paste it anywhere to share.'); }
  catch { toast('Your browser didn\'t let me copy. Select the link and press Ctrl C.'); }
}

export function SharePanel({ id, title, onClose }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api(`/api/docs/${id}/share`).then(setInfo).catch(setErr);
  useEffect(() => { if (online.value) load(); }, [id, online.value]);

  const change = async body => {
    setBusy(true);
    try { setInfo(await api(`/api/docs/${id}/share`, { method: 'POST', body })); }
    catch (e) { toast(e, { kind: 'error' }); }
    finally { setBusy(false); }
  };

  let content;
  if (!online.value) content = <ProtoCard small title="Sharing needs the internet" text="Your doc is safe on this device. Come back here when you're online." />;
  else if (err) content = <ProtoCard small error={err} />;
  else if (!info) content = <Spinner />;
  else {
    content = (
      <>
        <p class="hint">Anyone with a link can open "{title || 'Untitled document'}". Editing needs a Docs Protocol account.</p>
        <LinkRow label="Anyone with the link can view" icon="eye" token={info.linkView} busy={busy}
          onToggle={on => change({ link: 'view', on })} onReset={() => change({ link: 'view', on: true, reset: true })} />
        <LinkRow label="Anyone with the link can edit" icon="pencil" token={info.linkEdit} busy={busy}
          onToggle={on => change({ link: 'edit', on })} onReset={() => change({ link: 'edit', on: true, reset: true })} />
        <h3 class="panel-sub">People with access</h3>
        {!info.members.length ? <p class="hint">Just you so far.</p> : (
          <ul class="members">
            {info.members.map(m => (
              <li key={m.userId}>
                <Avatar name={m.name} seed={m.userId} size={30} />
                <span class="grow">{m.name}</span>
                <select aria-label={`What ${m.name} can do`} value={m.role} disabled={busy}
                  onChange={e => change({ member: { userId: m.userId, role: e.target.value } })}>
                  <option value="view">Can view</option>
                  <option value="edit">Can edit</option>
                </select>
                <IconButton icon="close" label={`Remove ${m.name}`} onClick={async () => {
                  if (await confirmDialog({ title: `Remove ${m.name}?`, text: 'They lose access right away. If a link is still on, they could open it again.', ok: 'Remove', danger: true })) change({ member: { userId: m.userId, role: 'none' } });
                }} />
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
  return <Panel title="Share" onClose={onClose}>{content}</Panel>;
}

function LinkRow({ label, icon, token, busy, onToggle, onReset }) {
  return (
    <div class={`linkrow ${token ? 'on' : ''}`}>
      <label class="switch-row">
        <Icon name={icon} size={18} />
        <span class="grow">{label}</span>
        <input type="checkbox" role="switch" checked={!!token} disabled={busy} onChange={e => onToggle(e.target.checked)} aria-label={label} />
      </label>
      {token && (
        <div class="linkbox">
          <input readOnly value={linkFor(token)} aria-label="Share link" onFocus={e => e.target.select()} />
          <Button size="sm" kind="primary" icon="copy" onClick={() => copy(linkFor(token))}>Copy</Button>
          <Button size="sm" onClick={onReset} title="Make a new link; the old one stops working">Reset</Button>
        </div>
      )}
    </div>
  );
}
